import { KsService, useApiClient } from '@siclop/kstone-vue'
import type { LeaveAccrual, LeaveBalance } from '../models'
import type { LeaveBalanceFilter } from '../filters'

/** Corps de la correction RH d'un solde. */
export interface LeaveBalanceAdjustment {
  /** Correction totale en jours (−365 à 365), remplace la précédente. */
  manualAdjustment: number
  /** Motif obligatoire. */
  reason: string
  /** Nouvelle échéance de prise (`YYYY-MM-DD`), null : échéance calculée. */
  extendedDeadline?: string | null
}

/** Périmètre d'un recalcul : un salarié, ou toute l'organisation. */
export interface LeaveBalanceRecomputeRequest {
  employeeId?: number
  periodStartYear?: number
  organizationId?: number
}

export class LeaveBalanceService extends KsService {
  protected static mainUrl = '/leave-balances'

  /**
   * Soldes filtrés (salarié, type, période, `openAt`) ; lève une erreur si l'API refuse (contrairement à `getAll`,
   * qui rend une liste vide).
   */
  static async list(filter: LeaveBalanceFilter): Promise<LeaveBalance[]> {
    return useApiClient().getAll<LeaveBalance>(this.buildFilterUrl(filter))
  }

  /** Acquisition mois par mois d'un solde. */
  static async accruals(id: number): Promise<LeaveAccrual[]> {
    return useApiClient().get<LeaveAccrual[]>(`${this.mainUrl}/${id}/accruals`)
  }

  /** Correction RH motivée (EDIT_LEAVEBALANCE + ALL_EMPLOYEES) ; renvoie le solde recalculé. */
  static async adjust(id: number, adjustment: LeaveBalanceAdjustment): Promise<LeaveBalance> {
    return useApiClient().postRaw<LeaveBalanceAdjustment, LeaveBalance>(`${this.mainUrl}/${id}/adjust`, adjustment)
  }

  /** Recalcule les soldes (idempotent) ; renvoie le nombre de salariés recalculés. */
  static async recompute(request: LeaveBalanceRecomputeRequest = {}): Promise<number> {
    const result = await useApiClient().postRaw<LeaveBalanceRecomputeRequest, { employeesRecomputed: number }>(
      `${this.mainUrl}/recompute`, request)
    return result?.employeesRecomputed ?? 0
  }
}
